import React, { useRef, useCallback } from 'react';
import {
  calculateRoundTableChairs,
  calculateRectTableChairs,
  calculateChairRowGroup,
  type ChairSides,
} from '@peles/shared';
import type { CanvasItem } from './types';
import { TABLE_OBJECT_TYPES, isTableItem } from './types';
import { ftToPx, getTableColor, CHAIR_SIZE_FT, visualAnchorFloor } from './helpers';

export function CanvasItemShape({
  item,
  scale,
  isSelected,
  seated = 0,
  onClick,
  onDragEnd,
  onDragMove,
  groupColor,
  showChairs = false,
  showLabel = true,
  showTableNumbers = true,
  showIcons = true,
  chairSizeFt = CHAIR_SIZE_FT,
  chairSides = 'top-bottom',
  seatColors,
  canvasRotation = 0,
}: {
  item: CanvasItem;
  scale: number;
  isSelected: boolean;
  seated?: number;
  onClick: (e?: React.MouseEvent) => void;
  onDragEnd: (x: number, y: number) => void;
  /** Live logical-px delta while dragging (for group/multi-select preview) */
  onDragMove?: (dx: number, dy: number) => void;
  groupColor?: string;
  showChairs?: boolean;
  showLabel?: boolean;
  showTableNumbers?: boolean;
  showIcons?: boolean;
  chairSizeFt?: number;
  chairSides?: ChairSides;
  /** seatNumber → color hex, used to color individual chairs by family group */
  seatColors?: Record<number, string>;
  canvasRotation?: 0 | 90 | 180 | 270;
}) {
  const isDragging = useRef(false);
  const didDrag = useRef(false);
  const dragStart = useRef({ x: 0, y: 0, origX: 0, origY: 0 });
  const elRef = useRef<SVGGElement>(null);
  const w = ftToPx(item.widthFt);
  const h = ftToPx(item.heightFt);
  const isTable = isTableItem(item);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      isDragging.current = true;
      didDrag.current = false;
      dragStart.current = { x: e.clientX, y: e.clientY, origX: item.x, origY: item.y };
      // Floor on the item's VISIBLE box (rotation-aware), not the raw anchor, so
      // a rotated non-square item can be dragged flush to the top/left wall (its
      // stored anchor goes negative). Un-rotated / square items floor at 0.
      const posFloor = visualAnchorFloor(w, h, item.rotation);

      const handleMouseMove = (ev: MouseEvent) => {
        if (!isDragging.current || !elRef.current) return;
        const vdx = (ev.clientX - dragStart.current.x) / scale;
        const vdy = (ev.clientY - dragStart.current.y) / scale;
        if (Math.abs(vdx) > 2 || Math.abs(vdy) > 2) didDrag.current = true;
        // Transform visual delta to logical delta based on canvas rotation
        let ldx: number, ldy: number;
        switch (canvasRotation) {
          case 90:
            ldx = vdy;
            ldy = -vdx;
            break;
          case 180:
            ldx = -vdx;
            ldy = -vdy;
            break;
          case 270:
            ldx = -vdy;
            ldy = vdx;
            break;
          default:
            ldx = vdx;
            ldy = vdy;
        }
        const nx = Math.max(posFloor.x, dragStart.current.origX + ldx);
        const ny = Math.max(posFloor.y, dragStart.current.origY + ldy);
        elRef.current.setAttribute(
          'transform',
          `translate(${nx}, ${ny}) rotate(${item.rotation}, ${w / 2}, ${h / 2})`,
        );
        // Report the clamped effective delta so co-moving items preview at
        // the same offset the drop will commit (matters past the 0 boundary).
        onDragMove?.(nx - dragStart.current.origX, ny - dragStart.current.origY);
      };

      const handleMouseUp = (ev: MouseEvent) => {
        if (!isDragging.current) return;
        isDragging.current = false;
        const vdx = (ev.clientX - dragStart.current.x) / scale;
        const vdy = (ev.clientY - dragStart.current.y) / scale;
        // Transform visual delta to logical delta based on canvas rotation
        let ldx: number, ldy: number;
        switch (canvasRotation) {
          case 90:
            ldx = vdy;
            ldy = -vdx;
            break;
          case 180:
            ldx = -vdx;
            ldy = -vdy;
            break;
          case 270:
            ldx = -vdy;
            ldy = vdx;
            break;
          default:
            ldx = vdx;
            ldy = vdy;
        }
        const nx = Math.max(posFloor.x, dragStart.current.origX + ldx);
        const ny = Math.max(posFloor.y, dragStart.current.origY + ldy);
        onDragEnd(nx, ny);
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };

      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    },
    [item.x, item.y, item.rotation, w, h, scale, onDragEnd, onDragMove, canvasRotation],
  );

  // ─── Determine shape ──────────────────────────────────────────
  const shape: string = (() => {
    if (item.config?.shape) return item.config.shape as string;
    // Table types → shape mapping
    if (
      item.objectType === 'TABLE_ROUND' ||
      item.objectType === 'TABLE_OVAL' ||
      item.objectType === 'COCKTAIL_TABLE'
    )
      return 'CIRCLE';
    if (item.objectType === 'TABLE_SERPENTINE') return 'SERPENTINE';
    if (TABLE_OBJECT_TYPES.has(item.objectType)) return 'RECTANGLE';
    // Legacy object shapes
    if (item.objectType === 'TABLE' && item.config?.tableShape === 'round') return 'CIRCLE';
    return 'RECTANGLE';
  })();

  // ─── Colors ───────────────────────────────────────────────────
  // Tables default to occupancy-based coloring (empty/partial/full), but a
  // user-chosen color (config.useCustomColor) always wins over occupancy.
  const hasCustomColor = !!item.config?.useCustomColor;
  const tableColors = isTable
    ? groupColor
      ? { fill: groupColor, stroke: '#6b7280' }
      : hasCustomColor
        ? { fill: item.color, stroke: '#6b7280' }
        : getTableColor(seated, item.seats)
    : null;

  const fillColor = tableColors ? tableColors.fill : item.color;
  const strokeColor = isSelected
    ? '#2563eb'
    : tableColors
      ? tableColors.stroke
      : item.objectType === 'CHAIR' && item.color && item.color !== '#9ca3af'
        ? item.color
        : ((item.config?.strokeColor as string | undefined) ?? '#6b7280');
  const strokeWidth = isSelected ? 3 : isTable ? 2 : 1;
  const opacity =
    item.objectType === 'AREA'
      ? item.opacity
      : isTable
        ? 1
        : item.objectType === 'CHAIR'
          ? (item.opacity ?? 1)
          : 0.85;

  // ─── Shape rendering ─────────────────────────────────────────
  const renderShape = () => {
    switch (shape) {
      case 'CIRCLE':
      case 'OVAL':
        return (
          <ellipse
            cx={w / 2}
            cy={h / 2}
            rx={w / 2}
            ry={h / 2}
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            opacity={opacity}
          />
        );
      case 'ROUNDED_RECT':
        return (
          <rect
            width={w}
            height={h}
            rx={Math.min(16, w / 4, h / 4)}
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            opacity={opacity}
          />
        );
      case 'DIAMOND': {
        const pts = `${w / 2},0 ${w},${h / 2} ${w / 2},${h} 0,${h / 2}`;
        return (
          <polygon
            points={pts}
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            opacity={opacity}
          />
        );
      }
      case 'SERPENTINE': {
        // Quarter-annulus banquet table: outer arc from the top-left corner to
        // the bottom-right corner (centered at the bottom-left), with an inner
        // arc at half the radius forming the serving curve.
        const d = [
          `M 0,0`,
          `A ${w},${h} 0 0 1 ${w},${h}`,
          `L ${w / 2},${h}`,
          `A ${w / 2},${h / 2} 0 0 0 0,${h / 2}`,
          'Z',
        ].join(' ');
        return (
          <path
            d={d}
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            opacity={opacity}
          />
        );
      }
      case 'HEXAGON': {
        const hw = w / 2,
          hh = h / 2;
        const pts = [
          `${hw * 0.5},0`,
          `${hw * 1.5},0`,
          `${w},${hh}`,
          `${hw * 1.5},${h}`,
          `${hw * 0.5},${h}`,
          `0,${hh}`,
        ].join(' ');
        return (
          <polygon
            points={pts}
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            opacity={opacity}
          />
        );
      }
      default: {
        // RECTANGLE — tables get different rx based on type
        const rx =
          item.objectType === 'TABLE_HEAD'
            ? 20
            : item.objectType === 'TABLE_SQUARE'
              ? 6
              : isTable
                ? 8
                : 4;
        return (
          <rect
            width={w}
            height={h}
            rx={rx}
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            opacity={opacity}
          />
        );
      }
    }
  };

  // ─── Chair preview (tables only) ──────────────────────────────
  const renderChairPreview = () => {
    if (!showChairs || !isTable || item.seats <= 0) return null;
    const isRound =
      item.objectType === 'TABLE_ROUND' ||
      item.objectType === 'TABLE_OVAL' ||
      (item.objectType === 'TABLE' && item.config?.tableShape === 'round') ||
      (item.objectType === 'TABLE' && item.config?.shape === 'CIRCLE') ||
      item.objectType === 'COCKTAIL_TABLE';
    const positions = isRound
      ? calculateRoundTableChairs(Math.max(item.widthFt, item.heightFt), item.seats, chairSizeFt)
      : calculateRectTableChairs(item.widthFt, item.heightFt, item.seats, chairSizeFt, chairSides);
    const cSz = ftToPx(chairSizeFt);
    const cx = w / 2;
    const cy = h / 2;
    return positions.map((pos, i) => {
      const seatNum = i + 1;
      const chairColor = seatColors?.[seatNum] ?? '#e5e7eb';
      const px = isRound ? cx + ftToPx(pos.x) - cSz / 2 : ftToPx(pos.x);
      const py = isRound ? cy + ftToPx(pos.y) - cSz / 2 : ftToPx(pos.y);
      return (
        <rect
          key={`chair-${i}`}
          x={px}
          y={py}
          width={cSz}
          height={cSz}
          rx={2}
          fill={chairColor}
          stroke={seatColors?.[seatNum] ? chairColor : '#9ca3af'}
          strokeWidth={0.5}
          transform={`rotate(${Math.round(pos.rotation)}, ${px + cSz / 2}, ${py + cSz / 2})`}
        />
      );
    });
  };

  // ─── Door / Window renderings ─────────────────────────────────
  const isDoor = item.objectType.startsWith('DOOR_');
  const isWindow = item.objectType === 'WINDOW';

  const renderDoorOrWindow = (): React.ReactNode => {
    if (!isDoor && !isWindow) return null;

    const sc = strokeColor;
    const sw = strokeWidth;

    if (isWindow) {
      // Three-line window symbol: outer frame + two glass lines
      return (
        <g>
          <rect width={w} height={h} fill={fillColor} stroke={sc} strokeWidth={sw} />
          <line x1={0} y1={h / 3} x2={w} y2={h / 3} stroke={sc} strokeWidth={0.8} />
          <line x1={0} y1={(h * 2) / 3} x2={w} y2={(h * 2) / 3} stroke={sc} strokeWidth={0.8} />
        </g>
      );
    }

    const ot = item.objectType;
    const isInward = ot.includes('_IN_');
    const isBoth = ot.endsWith('_BOTH');
    const isLeftHinge = !ot.endsWith('_LEFT'); // RIGHT variants have hinge on left side

    if (ot === 'DOOR_SLIDING') {
      const track = Math.max(3, h / 5);
      const panelW = w * 0.55;
      const overlap = w * 0.1;
      return (
        <g>
          {/* Track lines */}
          <rect width={w} height={track} fill="#9ca3af" stroke={sc} strokeWidth={1} />
          <rect width={w} height={track} y={h - track} fill="#9ca3af" stroke={sc} strokeWidth={1} />
          {/* Left panel */}
          <rect
            x={0}
            y={track}
            width={panelW}
            height={h - track * 2}
            fill={fillColor}
            stroke={sc}
            strokeWidth={sw}
          />
          {/* Right panel (overlapping) */}
          <rect
            x={w - panelW}
            y={track}
            width={panelW}
            height={h - track * 2}
            fill={fillColor}
            stroke={sc}
            strokeWidth={sw}
            opacity={0.7}
          />
          {/* Overlap indicator */}
          <line
            x1={panelW - overlap}
            y1={track}
            x2={panelW - overlap}
            y2={h - track}
            stroke="#9ca3af"
            strokeWidth={1}
            strokeDasharray="3,2"
          />
        </g>
      );
    }

    // Swing doors — wall line at top for inward, bottom for outward
    // For inward: hinge & arc fill downward (positive y)
    // For outward: wall line at y=h, panel & arc fill upward (toward y=0)
    const wallY = isInward ? 0 : h;
    const swingDir = isInward ? 1 : -1; // +1 = downward, -1 = upward

    if (isBoth) {
      const hw = w / 2;
      // Left panel: hinge at (0, wallY), panel toward swingDir * h, arc to (hw, wallY)
      const lPanelEndY = wallY + swingDir * h;
      // Right panel: hinge at (w, wallY), panel toward swingDir * h, arc to (hw, wallY)
      const rPanelEndY = wallY + swingDir * h;
      const lSweep = isInward ? 1 : 0;
      const rSweep = isInward ? 0 : 1;
      return (
        <g>
          <line x1={0} y1={wallY} x2={w} y2={wallY} stroke={sc} strokeWidth={sw * 2} />
          {/* Left half */}
          <line x1={0} y1={wallY} x2={0} y2={lPanelEndY} stroke={sc} strokeWidth={sw} />
          <path
            d={`M 0,${lPanelEndY} A ${h},${h} 0 0 ${lSweep} ${hw},${wallY}`}
            fill="none"
            stroke={sc}
            strokeWidth={0.8}
            strokeDasharray="4,2"
          />
          {/* Right half */}
          <line x1={w} y1={wallY} x2={w} y2={rPanelEndY} stroke={sc} strokeWidth={sw} />
          <path
            d={`M ${w},${rPanelEndY} A ${h},${h} 0 0 ${rSweep} ${hw},${wallY}`}
            fill="none"
            stroke={sc}
            strokeWidth={0.8}
            strokeDasharray="4,2"
          />
        </g>
      );
    }

    // Single swing door
    const panelLen = Math.min(w, h);
    if (isLeftHinge) {
      // Hinge at left side of wall line
      const hingeX = 0;
      const panelEndY = wallY + swingDir * panelLen;
      const arcSweep = isInward ? 1 : 0;
      return (
        <g>
          <line x1={0} y1={wallY} x2={w} y2={wallY} stroke={sc} strokeWidth={sw * 2} />
          <line x1={hingeX} y1={wallY} x2={hingeX} y2={panelEndY} stroke={sc} strokeWidth={sw} />
          <path
            d={`M ${hingeX},${panelEndY} A ${panelLen},${panelLen} 0 0 ${arcSweep} ${w},${wallY}`}
            fill="none"
            stroke={sc}
            strokeWidth={0.8}
            strokeDasharray="4,2"
          />
        </g>
      );
    } else {
      // Hinge at right side of wall line
      const hingeX = w;
      const panelEndY = wallY + swingDir * panelLen;
      const arcSweep = isInward ? 0 : 1;
      return (
        <g>
          <line x1={0} y1={wallY} x2={w} y2={wallY} stroke={sc} strokeWidth={sw * 2} />
          <line x1={hingeX} y1={wallY} x2={hingeX} y2={panelEndY} stroke={sc} strokeWidth={sw} />
          <path
            d={`M ${hingeX},${panelEndY} A ${panelLen},${panelLen} 0 0 ${arcSweep} 0,${wallY}`}
            fill="none"
            stroke={sc}
            strokeWidth={0.8}
            strokeDasharray="4,2"
          />
        </g>
      );
    }
  };

  // ─── Special child renderings ─────────────────────────────────
  const renderCeremonyChairs = () => {
    if (item.objectType !== 'CEREMONY_CHAIRS' || !Array.isArray(item.config?.positions))
      return null;
    return (item.config.positions as { x: number; y: number }[]).map((pos, ci) => (
      <rect
        key={`cc-${ci}`}
        x={ftToPx(pos.x)}
        y={ftToPx(pos.y)}
        width={ftToPx(CHAIR_SIZE_FT)}
        height={ftToPx(CHAIR_SIZE_FT)}
        rx={2}
        fill="#9ca3af"
        stroke="#6b7280"
        strokeWidth={0.5}
        opacity={0.85}
      />
    ));
  };

  const renderChairRows = () => {
    if (item.objectType !== 'CHAIR_ROW') return null;
    const rows = (item.config.rows as number) || 1;
    const seatsPerRow = (item.config.seatsPerRow as number) || 10;
    const chairSpacing = (item.config.chairSpacingFt as number) || 2;
    const rowSpacing = (item.config.rowSpacingFt as number) || 3;
    const group = calculateChairRowGroup(rows, seatsPerRow, chairSpacing, rowSpacing);
    return group.chairs.map((ch, ci) => (
      <rect
        key={`chr-${ci}`}
        x={ftToPx(ch.x)}
        y={ftToPx(ch.y)}
        width={ftToPx(1.5)}
        height={ftToPx(1.5)}
        rx={2}
        fill="#9ca3af"
        stroke="#6b7280"
        strokeWidth={0.5}
        opacity={0.6}
      />
    ));
  };

  // ─── Test ID ──────────────────────────────────────────────────
  const testId = isTable ? `canvas-table-${item.tableNumber}` : `canvas-object-${item.sortOrder}`;

  return (
    <g
      ref={elRef}
      transform={`translate(${item.x}, ${item.y}) rotate(${item.rotation}, ${w / 2}, ${h / 2})`}
      onMouseDown={handleMouseDown}
      onClick={(e) => {
        e.stopPropagation();
        // The click that follows a real drag must not change the selection
        // (it would collapse a multi-select to just the dragged item).
        if (didDrag.current) {
          didDrag.current = false;
          return;
        }
        onClick(e);
      }}
      style={{ cursor: 'grab' }}
      data-testid={testId}
    >
      {renderChairPreview()}
      {isDoor || isWindow ? renderDoorOrWindow() : renderShape()}
      {renderCeremonyChairs()}
      {renderChairRows()}

      {/* Table number (independent of label toggle) */}
      {isTable && showTableNumbers && (
        <text
          x={w / 2}
          y={h / 2 - (item.label && showLabel ? 14 : 6)}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={14}
          fontWeight="bold"
          fill="#374151"
        >
          #{item.tableNumber}
        </text>
      )}

      {/* Label */}
      {!!showLabel && (
        <text
          x={w / 2}
          y={
            isDoor || isWindow
              ? h + 10
              : h / 2 + (isTable && showTableNumbers ? 2 : isTable || item.seats > 0 ? -6 : 0)
          }
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={isTable ? 11 : Math.min(12, w / 6)}
          fontWeight="600"
          fill={isTable && showTableNumbers ? '#6b7280' : '#374151'}
        >
          {item.label || (isTable ? '' : isDoor || isWindow ? '' : item.objectType)}
        </text>
      )}

      {/* Icon */}
      {showIcons && !!item.config?.icon && (
        <text
          x={w / 2}
          y={Math.max(12, h / 2 - (isTable && showTableNumbers ? 22 : 14))}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={Math.min(20, w / 2.5, h / 2.5)}
          fill="#555"
        >
          {String(item.config.icon)}
        </text>
      )}

      {/* Seat count */}
      {isTable ? (
        <text x={w / 2} y={h / 2 + 12} textAnchor="middle" fontSize="11" fill="#6b7280">
          {seated}/{item.seats}
        </text>
      ) : (
        item.seats > 0 && (
          <text
            x={w / 2}
            y={h / 2 + 10}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize="9"
            fill="#6b7280"
          >
            {item.seats} seats
          </text>
        )
      )}

      {/* Dimensions (non-table objects) */}
      {!isTable && (
        <text x={w / 2} y={h + 12} textAnchor="middle" fontSize="8" fill="#9ca3af">
          {item.widthFt}&apos; x {item.heightFt}&apos;
        </text>
      )}
    </g>
  );
}
